"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import type { CSSProperties, MouseEvent, MouseEventHandler, ReactNode, RefObject } from "react";

import { HourglassLoader } from "@/components/HourglassLoader";
import { HourglassMark } from "@/components/HourglassMark";
import { LandingFeatureChecklist, SavingsSubline } from "@/components/pricing/PricingUiParts";
import {
  annualVsMonthlySavings,
  formatZarAmount,
  managerTierFeatures,
  ownerTierFeatures,
  PRICING_FOOTNOTE_RENEWAL,
  staffTierFeatures,
  MANAGER_MONTHLY_ZAR,
  MANAGER_YEARLY_ZAR,
  OWNER_MONTHLY_ZAR,
  OWNER_YEARLY_ZAR,
} from "@/lib/pricingDisplay";

const managerAnnualSave = annualVsMonthlySavings(MANAGER_MONTHLY_ZAR, MANAGER_YEARLY_ZAR);
const ownerAnnualSave = annualVsMonthlySavings(OWNER_MONTHLY_ZAR, OWNER_YEARLY_ZAR);

type BaseAnimatedProps = {
  style?: CSSProperties;
  delay?: number;
};

type RevealProps = BaseAnimatedProps & {
  children: ReactNode;
};

type SplitWordsProps = BaseAnimatedProps & {
  text: string;
  wStyle?: CSSProperties;
  /** Use `h1` once on the page for SEO and screen readers. */
  as?: "div" | "h1";
};

type CinematicStatementProps = {
  id?: string;
  lines: string[];
  bg?: string;
  textColor?: string;
  align?: "left" | "center" | "right";
  sub?: string | null;
};

type ScaleGroupProps = {
  children: ReactNode;
  gap?: number;
  horizontal?: boolean;
};

type IPhone17ProProps = {
  screen: ReactNode;
  label: string;
};

type SectionBlockProps = {
  children: ReactNode;
  background: string;
  padding: string;
  id?: string;
  style?: CSSProperties;
};

type NavItem = {
  label: string;
  href: string;
};

const C = {
  navyDeep:"#0A1A2A",navy:"#0F172A",navyMid:"#1E293B",
  teal:"#1DB8C4",tealDim:"#17A0AB",tealPale:"#E8F8FA",
  tealSoft:"rgba(29,184,196,0.10)",tealGlow:"rgba(29,184,196,0.22)",
  surface:"#EEF5FB",surfaceMid:"#E2EDF6",card:"#FFFFFF",
  border:"#D2E4F0",muted:"#6B82A0",
  wRed:"#EF4444",wAmber:"#F59E0B",wSafe:"#10B981",
};

/** Shared hover for pulsating signup CTAs (CSS keyframe `ctaPulse` + inline pause on hover). */
function ctaPulseHoverHandlers(idleShadow: string, hoverShadow: string, scale: number) {
  return {
    onMouseEnter(e: MouseEvent<HTMLAnchorElement>) {
      e.currentTarget.style.animation = "none";
      e.currentTarget.style.transform = `scale(${scale})`;
      e.currentTarget.style.boxShadow = hoverShadow;
    },
    onMouseLeave(e: MouseEvent<HTMLAnchorElement>) {
      e.currentTarget.style.transform = "scale(1)";
      e.currentTarget.style.boxShadow = idleShadow;
      e.currentTarget.style.animation = "ctaPulse 2.4s ease-in-out infinite";
    },
  };
}

const readyToStopPulseHover = ctaPulseHoverHandlers(
  `0 4px 24px ${C.tealGlow}`,
  "0 14px 34px rgba(29,184,196,0.34)",
  1.07,
);

const navTextLinkStyle: CSSProperties = {
  color: C.muted,
  textDecoration: "none",
  fontSize: 14,
};

const footerTextLinkStyle: CSSProperties = {
  fontSize: 13,
  color: "rgba(255,255,255,0.3)",
  textDecoration: "none",
};

const primarySignupCtaBaseStyle: CSSProperties = {
  background: C.teal,
  color: C.navyDeep,
  border: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
};

const baseCardStyle: CSSProperties = {
  background: C.card,
  border: `1px solid ${C.border}`,
};

const problemCardStyle: CSSProperties = {
  ...baseCardStyle,
  borderRadius: 18,
  padding: 32,
  position: "relative",
  overflow: "hidden",
  height: "100%",
};

const pricingCardStyle = (isPopular: boolean): CSSProperties => ({
  background: C.card,
  border: `${isPopular ? 2 : 1}px solid ${isPopular ? C.teal : C.border}`,
  borderRadius: 20,
  padding: 32,
  position: "relative",
  boxShadow: isPopular ? `0 0 0 1px rgba(29,184,196,0.1),0 20px 48px rgba(29,184,196,0.08)` : "none",
});

const NAV_ITEMS: NavItem[] = [
  { label: "Features", href: "#features" },
  { label: "How It Works", href: "#point-tap-done" },
  { label: "Pricing", href: "#pricing" },
  { label: "Blog", href: "/blog" },
];

type AmbientHourglassSpec = {
  top: string;
  left: string;
  size: number;
  color: string;
  orbitPx: number;
  orbitSec: number;
  spinSec: number;
  delaySec: number;
  opacity: number;
};

/** Scattered faint hourglasses — smooth circular drift + slow rotation (no stepped easing). */
const LANDING_AMBIENT_HOURGLASSES: AmbientHourglassSpec[] = [
  { top: "3%", left: "5%", size: 20, color: "rgba(29,184,196,0.5)", orbitPx: 10, orbitSec: 40, spinSec: 29, delaySec: 0, opacity: 0.1 },
  { top: "7%", left: "88%", size: 28, color: "rgba(29,184,196,0.45)", orbitPx: 14, orbitSec: 34, spinSec: 25, delaySec: 0.5, opacity: 0.09 },
  { top: "11%", left: "42%", size: 16, color: "rgba(27,43,74,0.28)", orbitPx: 8, orbitSec: 44, spinSec: 32, delaySec: 1.1, opacity: 0.1 },
  { top: "15%", left: "72%", size: 24, color: "rgba(29,184,196,0.42)", orbitPx: 12, orbitSec: 36, spinSec: 28, delaySec: 0.2, opacity: 0.08 },
  { top: "19%", left: "18%", size: 18, color: "rgba(29,184,196,0.48)", orbitPx: 9, orbitSec: 42, spinSec: 31, delaySec: 0.8, opacity: 0.09 },
  { top: "24%", left: "94%", size: 20, color: "rgba(29,184,196,0.4)", orbitPx: 10, orbitSec: 39, spinSec: 26, delaySec: 1.4, opacity: 0.07 },
  { top: "28%", left: "8%", size: 26, color: "rgba(29,184,196,0.44)", orbitPx: 13, orbitSec: 33, spinSec: 24, delaySec: 0.3, opacity: 0.08 },
  { top: "32%", left: "55%", size: 14, color: "rgba(29,184,196,0.55)", orbitPx: 7, orbitSec: 46, spinSec: 34, delaySec: 2, opacity: 0.07 },
  { top: "36%", left: "30%", size: 22, color: "rgba(29,184,196,0.42)", orbitPx: 11, orbitSec: 37, spinSec: 27, delaySec: 0.6, opacity: 0.08 },
  { top: "41%", left: "78%", size: 18, color: "rgba(255,255,255,0.2)", orbitPx: 9, orbitSec: 41, spinSec: 30, delaySec: 0.9, opacity: 0.11 },
  { top: "45%", left: "12%", size: 24, color: "rgba(255,255,255,0.18)", orbitPx: 12, orbitSec: 35, spinSec: 26, delaySec: 0.15, opacity: 0.1 },
  { top: "48%", left: "62%", size: 16, color: "rgba(255,255,255,0.22)", orbitPx: 8, orbitSec: 43, spinSec: 32, delaySec: 1.2, opacity: 0.1 },
  { top: "52%", left: "38%", size: 20, color: "rgba(255,255,255,0.16)", orbitPx: 10, orbitSec: 38, spinSec: 28, delaySec: 0.4, opacity: 0.09 },
  { top: "56%", left: "90%", size: 22, color: "rgba(255,255,255,0.2)", orbitPx: 11, orbitSec: 36, spinSec: 27, delaySec: 0.7, opacity: 0.1 },
  { top: "60%", left: "5%", size: 18, color: "rgba(29,184,196,0.4)", orbitPx: 9, orbitSec: 40, spinSec: 29, delaySec: 1.6, opacity: 0.07 },
  { top: "64%", left: "48%", size: 26, color: "rgba(29,184,196,0.38)", orbitPx: 13, orbitSec: 32, spinSec: 23, delaySec: 0.25, opacity: 0.08 },
  { top: "68%", left: "22%", size: 15, color: "rgba(29,184,196,0.5)", orbitPx: 7, orbitSec: 45, spinSec: 33, delaySec: 1.8, opacity: 0.06 },
  { top: "72%", left: "82%", size: 20, color: "rgba(27,43,74,0.3)", orbitPx: 10, orbitSec: 39, spinSec: 28, delaySec: 0.5, opacity: 0.09 },
  { top: "76%", left: "14%", size: 24, color: "rgba(29,184,196,0.42)", orbitPx: 12, orbitSec: 34, spinSec: 25, delaySec: 1.1, opacity: 0.08 },
  { top: "80%", left: "58%", size: 17, color: "rgba(29,184,196,0.48)", orbitPx: 9, orbitSec: 42, spinSec: 31, delaySec: 0.35, opacity: 0.07 },
  { top: "84%", left: "36%", size: 21, color: "rgba(255,255,255,0.12)", orbitPx: 11, orbitSec: 37, spinSec: 27, delaySec: 1.3, opacity: 0.13 },
  { top: "88%", left: "70%", size: 19, color: "rgba(255,255,255,0.14)", orbitPx: 10, orbitSec: 41, spinSec: 30, delaySec: 0.55, opacity: 0.12 },
  { top: "92%", left: "8%", size: 23, color: "rgba(255,255,255,0.11)", orbitPx: 12, orbitSec: 36, spinSec: 26, delaySec: 0.9, opacity: 0.13 },
  { top: "96%", left: "52%", size: 16, color: "rgba(255,255,255,0.13)", orbitPx: 8, orbitSec: 44, spinSec: 32, delaySec: 2.1, opacity: 0.1 },
];

function OrbitingHourglass({
  size,
  color,
  orbitPx,
  orbitSec,
  spinSec,
  delaySec = 0,
  style,
}: {
  size: number;
  color: string;
  orbitPx: number;
  orbitSec: number;
  spinSec: number;
  delaySec?: number;
  style?: CSSProperties;
}) {
  const h = Math.round((size * 48) / 40);
  return (
    <div aria-hidden style={{ position: "absolute", pointerEvents: "none", zIndex: 0, ...style }}>
      <div
        style={{
          width: 0,
          height: 0,
          ...({ "--hourglass-r": `${orbitPx}px` } as CSSProperties),
          animation: `hourglassOrbit ${orbitSec}s linear infinite`,
          animationDelay: `${delaySec}s`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: size,
            height: h,
            marginLeft: -size / 2,
            marginTop: -h / 2,
            animation: `hourglassSpinSmooth ${spinSec}s linear infinite`,
            animationDelay: `${delaySec * 0.62}s`,
          }}
        >
          <HourglassMark size={size} color={color} />
        </div>
      </div>
    </div>
  );
}

function LandingHourglassField() {
  return (
    <>
      {LANDING_AMBIENT_HOURGLASSES.map((s, i) => (
        <OrbitingHourglass
          key={i}
          size={s.size}
          color={s.color}
          orbitPx={s.orbitPx}
          orbitSec={s.orbitSec}
          spinSec={s.spinSec}
          delaySec={s.delaySec}
          style={{ top: s.top, left: s.left, opacity: s.opacity }}
        />
      ))}
    </>
  );
}

const setLinkColor = (event: MouseEvent<HTMLElement>, color: string) => {
  event.currentTarget.style.color = color;
};

const withHoverColor = (baseColor: string): {
  onMouseEnter: MouseEventHandler<HTMLElement>;
  onMouseLeave: MouseEventHandler<HTMLElement>;
} => ({
  onMouseEnter: (event) => setLinkColor(event, C.teal),
  onMouseLeave: (event) => setLinkColor(event, baseColor),
});

function SectionBlock({ children, background, padding, id, style }: SectionBlockProps) {
  return (
    <section id={id} style={{background, padding, ...style}}>
      {children}
    </section>
  );
}

function useCanHover() {
  const [canHover, setCanHover] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setCanHover(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return canHover;
}

function useReveal(threshold=0.1): [RefObject<HTMLElement | null>, boolean] {
  const ref=useRef<HTMLElement | null>(null);
  const [vis,setVis]=useState(false);
  useEffect(()=>{
    const obs=new IntersectionObserver(([e])=>{if(e.isIntersecting){setVis(true);obs.disconnect();}},{threshold});
    if(ref.current)obs.observe(ref.current);
    return()=>obs.disconnect();
  },[threshold]);
  return [ref,vis];
}

function Reveal({children,delay=0,style={}}: RevealProps) {
  const [ref,vis]=useReveal();
  return <div ref={ref as RefObject<HTMLDivElement>} style={{opacity:vis?1:0,transform:vis?"translateY(0)":"translateY(28px)",transition:`opacity 0.7s ${delay}s ease,transform 0.7s ${delay}s ease`,...style}}>{children}</div>;
}

// Word-by-word slide-up
function SplitWords({text,style={},wStyle={},delay=0,as="div"}: SplitWordsProps) {
  const [ref,vis]=useReveal(0.08);
  const words=text.split(/\s+/).filter(Boolean);
  const Tag = as === "h1" ? "h1" : "div";
  return (
    <Tag
      ref={ref as RefObject<HTMLDivElement | HTMLHeadingElement>}
      style={{
        display:"flex",
        flexWrap:"wrap",
        alignItems:"baseline",
        gap:"0.32em",
        margin:0,
        fontWeight:"inherit",
        ...wStyle,
        ...style,
      }}
    >
      {words.map((w,i)=>(
        <span
          key={i}
          style={{
            display:"inline-block",
            opacity:vis?1:0,
            transform:vis?"translateY(0)":"translateY(22px)",
            transition:`opacity 0.55s ${delay+i*0.06}s cubic-bezier(0.22,1,0.36,1),transform 0.55s ${delay+i*0.06}s cubic-bezier(0.22,1,0.36,1)`,
          }}
        >
          {w}
        </span>
      ))}
    </Tag>
  );
}

// Cinematic statement block (replaces section labels)
function CinematicStatement({id,lines,bg=C.card,textColor=C.navy,align="left",sub=null}: CinematicStatementProps) {
  const [ref,vis]=useReveal(0.08);
  return (
    <div id={id} ref={ref as RefObject<HTMLDivElement>} style={{background:bg,padding:"80px 8%",textAlign:align,position:"relative",overflow:"hidden"}}>
      <OrbitingHourglass
        size={200}
        color="rgba(29,184,196,0.42)"
        orbitPx={22}
        orbitSec={54}
        spinSec={40}
        delaySec={0.6}
        style={{
          right: align === "left" ? "3%" : "auto",
          left: align === "left" ? "auto" : "3%",
          top: "50%",
          transform: "translateY(-50%)",
          opacity: 0.055,
        }}
      />
      {lines.map((line,i)=>(
        <div key={i} style={{fontFamily:"Georgia,serif",fontSize:"clamp(22px,3.8vw,48px)",fontWeight:700,
          letterSpacing:"-1.2px",lineHeight:1.18,color:textColor==="white"?"#fff":textColor,
          marginBottom:i<lines.length-1?10:sub?20:0,
          opacity:vis?1:0,transform:vis?"translateX(0)":"translateX(-36px)",
          transition:`opacity 0.85s ${i*0.2}s cubic-bezier(0.22,1,0.36,1),transform 0.85s ${i*0.2}s cubic-bezier(0.22,1,0.36,1)`}}>
          {line}
        </div>
      ))}
      {sub&&<p style={{fontSize:17,color:textColor==="white"?"rgba(255,255,255,0.45)":C.muted,maxWidth:520,lineHeight:1.75,fontWeight:300,whiteSpace:"pre-line",
        opacity:vis?1:0,transition:`opacity 0.85s ${lines.length*0.2+0.2}s ease`}}>{sub}</p>}
    </div>
  );
}

// 3D tilt card
function TiltCard({children,style={}}: { children: ReactNode; style?: CSSProperties }) {
  const canHover = useCanHover();
  const [t,setT]=useState({x:0,y:0,on:false});
  const mv=useCallback((e: MouseEvent<HTMLDivElement>)=>{
    if (!canHover) return;
    const r=e.currentTarget.getBoundingClientRect();
    setT({x:((e.clientY-r.top-r.height/2)/r.height)*7,y:((e.clientX-r.left-r.width/2)/r.width)*-7,on:true});
  },[canHover]);
  const lv=useCallback(()=>setT({x:0,y:0,on:false}),[]);
  return (
    <div onMouseMove={canHover ? mv : undefined} onMouseLeave={canHover ? lv : undefined}
      style={{transform:canHover ? `perspective(900px) rotateX(${t.x}deg) rotateY(${t.y}deg)` : "none",transition:"transform 0.15s ease,box-shadow 0.3s ease",
        boxShadow:t.on?`0 20px 50px rgba(27,43,74,0.12),0 0 0 1px rgba(29,184,196,0.18)`:"none",willChange:"transform",...style}}>
      {children}
    </div>
  );
}

// Scale-hover group: hovered item grows, siblings shrink slightly
function ScaleGroup({children,gap=24,horizontal=true}: ScaleGroupProps) {
  const canHover = useCanHover();
  const [hov,setHov]=useState<number | null>(null);
  const items=Array.isArray(children)?children:[children];
  return (
    <div style={{display:"flex",flexDirection:horizontal?"row":"column",gap,flexWrap:"wrap"}}>
      {items.map((child,i)=>(
        <div key={i} onMouseEnter={canHover ? ()=>setHov(i) : undefined} onMouseLeave={canHover ? ()=>setHov(null) : undefined}
          style={{flex:1,transition:"transform 0.4s cubic-bezier(0.22,1,0.36,1),opacity 0.3s ease",
            transform:!canHover || hov===null?"scale(1)":hov===i?"scale(1.04)":"scale(0.96)",
            opacity:!canHover || hov===null?1:hov===i?1:0.8}}>
          {child}
        </div>
      ))}
    </div>
  );
}

// Realistic iPhone 17 Pro mockup
function IPhone17Pro({screen,label}: IPhone17ProProps) {
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14,flex:1,maxWidth:230}}>
      <div style={{fontSize:11,fontWeight:600,color:C.teal,letterSpacing:"0.6px",textTransform:"uppercase",
        background:C.tealPale,padding:"5px 14px",borderRadius:100,border:`1px solid rgba(29,184,196,0.22)`}}>{label}</div>
      {/* Phone outer — titanium finish */}
      <div style={{position:"relative",width:"100%",aspectRatio:"393/852",
        background:"linear-gradient(160deg,#b0b0b5 0%,#6c6c72 25%,#9a9a9f 50%,#6c6c72 75%,#adadb2 100%)",
        borderRadius:46,padding:"4px 4px",
        boxShadow:`0 0 0 1px rgba(0,0,0,0.45),0 32px 90px rgba(0,0,0,0.45),inset 0 1px 0 rgba(255,255,255,0.22),0 4px 16px rgba(0,0,0,0.3)`}}>
        {/* Screen glass */}
        <div style={{width:"100%",height:"100%",background:"#0a0a0a",borderRadius:43,overflow:"hidden",position:"relative"}}>
          {/* Dynamic Island */}
          <div style={{position:"absolute",top:11,left:"50%",transform:"translateX(-50%)",
            width:"30%",height:26,background:"#000",borderRadius:18,zIndex:10,
            boxShadow:"0 0 0 1px rgba(255,255,255,0.04)"}}>
            <div style={{position:"absolute",right:"24%",top:"50%",transform:"translateY(-50%)",
              width:7,height:7,borderRadius:"50%",background:"#111",
              boxShadow:"inset 0 0 0 1.5px rgba(255,255,255,0.06)"}}/>
          </div>
          {/* Screen content */}
          <div style={{width:"100%",height:"100%",paddingTop:46}}>{screen}</div>
          {/* Home indicator */}
          <div style={{position:"absolute",bottom:7,left:"50%",transform:"translateX(-50%)",
            width:"34%",height:4,background:"rgba(255,255,255,0.45)",borderRadius:2}}/>
          {/* Screen sheen */}
          <div style={{position:"absolute",inset:0,background:"linear-gradient(145deg,rgba(255,255,255,0.04) 0%,transparent 60%)",pointerEvents:"none",borderRadius:43}}/>
        </div>
        {/* Power button right */}
        <div style={{position:"absolute",right:-4,top:"32%",width:4,height:58,
          background:"linear-gradient(to right,#6c6c72,#adadb2)",borderRadius:"0 3px 3px 0"}}/>
        {/* Action button left */}
        <div style={{position:"absolute",left:-4,top:"20%",width:4,height:22,
          background:"linear-gradient(to left,#6c6c72,#adadb2)",borderRadius:"3px 0 0 3px"}}/>
        {/* Vol up */}
        <div style={{position:"absolute",left:-4,top:"28%",width:4,height:34,
          background:"linear-gradient(to left,#6c6c72,#adadb2)",borderRadius:"3px 0 0 3px"}}/>
        {/* Vol down */}
        <div style={{position:"absolute",left:-4,top:"37%",width:4,height:34,
          background:"linear-gradient(to left,#6c6c72,#adadb2)",borderRadius:"3px 0 0 3px"}}/>
      </div>
    </div>
  );
}

// Thabo Molefe illustrated photo
function ThaboPhoto() {
  return (
    <div style={{width:"100%",maxWidth:340,borderRadius:18,overflow:"hidden",
      boxShadow:`0 20px 60px rgba(27,43,74,0.18)`,position:"relative"}}>
      <svg viewBox="0 0 340 260" style={{width:"100%",display:"block"}}>
        <defs>
          <linearGradient id="bg1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#EEF5FB"/><stop offset="100%" stopColor="#C5D8EC"/>
          </linearGradient>
          <linearGradient id="shelf1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#D2E4F0"/><stop offset="100%" stopColor="#B8CCE0"/>
          </linearGradient>
        </defs>
        <rect width="340" height="260" fill="url(#bg1)"/>
        {/* Pharmacy shelves */}
        {[55,100,148].map((y)=>(
          <g key={y}>
            <rect x="15" y={y} width="155" height="5" fill="url(#shelf1)" rx="2"/>
            {[18,36,56,76,98,118,138].map((x,bi)=>(
              <rect key={x} x={x} y={y-20} width={16} height={20}
                fill={[C.navy,C.teal,"#233257",C.tealDim,C.navy,"#1a3a5c",C.teal][bi]}
                rx="2" opacity="0.85"/>
            ))}
          </g>
        ))}
        {/* Floor suggestion */}
        <rect x="0" y="240" width="340" height="20" fill="#D2E4F0" opacity="0.5"/>
        {/* Body shadow */}
        <ellipse cx="230" cy="257" rx="55" ry="7" fill="rgba(0,0,0,0.12)"/>
        {/* Legs */}
        <rect x="210" y="210" width="18" height="50" rx="6" fill="#1B2B4A"/>
        <rect x="234" y="210" width="18" height="50" rx="6" fill="#1B2B4A"/>
        {/* Lab coat body */}
        <path d="M 185,135 L 275,135 L 282,260 L 178,260 Z" fill="#F5F8FA"/>
        {/* Shirt under coat */}
        <path d="M 200,135 L 260,135 L 265,260 L 195,260 Z" fill="#1B2B4A" opacity="0.8"/>
        {/* Lab coat lapels */}
        <path d="M 200,135 L 225,175 L 222,260 L 192,260 L 185,135 Z" fill="white" opacity="0.97"/>
        <path d="M 260,135 L 235,175 L 238,260 L 268,260 L 275,135 Z" fill="white" opacity="0.97"/>
        {/* Lab coat detail — pocket */}
        <rect x="192" y="180" width="22" height="16" rx="3" fill="none" stroke="#D2E4F0" strokeWidth="1.2"/>
        {/* Right arm outstretched holding phone */}
        <path d="M 185,145 L 148,172 L 142,210 L 156,213 L 160,178 L 196,158 Z" fill="white" opacity="0.97"/>
        {/* Left arm relaxed */}
        <path d="M 275,148 L 308,178 L 305,210 L 292,208 L 293,180 L 268,158 Z" fill="white" opacity="0.95"/>
        {/* Hand (right) */}
        <ellipse cx="149" cy="218" rx="11" ry="9" fill="#3D1F0D"/>
        {/* iPhone in right hand — pointing toward shelf */}
        <rect x="132" y="205" width="22" height="40" rx="5" fill="#111"/>
        <rect x="135" y="208" width="16" height="34" rx="4" fill={C.teal} opacity="0.9"/>
        {/* Dynamic Island on phone */}
        <rect x="140" y="210" width="6" height="3" rx="2" fill="#000"/>
        {/* Phone screen glint */}
        <rect x="136" y="215" width="10" height="2" rx="1" fill="rgba(255,255,255,0.7)"/>
        <rect x="136" y="219" width="7" height="1.5" rx="1" fill="rgba(255,255,255,0.45)"/>
        {/* Neck */}
        <rect x="218" y="108" width="22" height="30" rx="8" fill="#3D1F0D"/>
        {/* Head */}
        <ellipse cx="229" cy="85" rx="33" ry="36" fill="#3D1F0D"/>
        {/* Hair */}
        <ellipse cx="229" cy="54" rx="31" ry="14" fill="#1a0f08"/>
        <path d="M 196,75 Q 200,52 229,50 Q 258,52 262,75" fill="#1a0f08"/>
        {/* Eyes */}
        <ellipse cx="216" cy="80" rx="5.5" ry="6" fill="#1a0f08"/>
        <ellipse cx="242" cy="80" rx="5.5" ry="6" fill="#1a0f08"/>
        <ellipse cx="218" cy="79" rx="2" ry="2" fill="white" opacity="0.9"/>
        <ellipse cx="244" cy="79" rx="2" ry="2" fill="white" opacity="0.9"/>
        {/* Glasses */}
        <circle cx="216" cy="80" r="9" fill="none" stroke="#1a0f08" strokeWidth="1.5" opacity="0.45"/>
        <circle cx="242" cy="80" r="9" fill="none" stroke="#1a0f08" strokeWidth="1.5" opacity="0.45"/>
        <line x1="225" y1="80" x2="233" y2="80" stroke="#1a0f08" strokeWidth="1.5" opacity="0.45"/>
        <line x1="196" y1="80" x2="207" y2="80" stroke="#1a0f08" strokeWidth="1.5" opacity="0.3"/>
        <line x1="251" y1="80" x2="260" y2="80" stroke="#1a0f08" strokeWidth="1.5" opacity="0.3"/>
        {/* Nose */}
        <path d="M 224,89 Q 229,96 234,89" fill="none" stroke="#2a0f08" strokeWidth="1.5"/>
        {/* Big warm smile */}
        <path d="M 213,102 Q 229,118 245,102" fill="white" opacity="0.85"/>
        <path d="M 213,102 Q 229,116 245,102" fill="none" stroke="#1a0f08" strokeWidth="2.2" strokeLinecap="round"/>
        {/* Caption */}
        <rect x="0" y="228" width="340" height="32" fill="rgba(17,30,53,0.78)"/>
        <text x="170" y="249" textAnchor="middle" fill="white" fontSize="11.5" fontFamily="DM Sans,sans-serif" fontWeight="500">
          Thabo Molefe · MedCity Pharmacy, Johannesburg
        </text>
      </svg>
    </div>
  );
}



// ── Dashboard mock
const DASH_MOCK_EXPIRY_MONTH = 7; // July
const DASH_MOCK_EXPIRY_YEAR = 2026;

function getDaysUntilMonthEnd(now: Date, month: number, year: number) {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiryMonthEnd = new Date(year, month, 0, 23, 59, 59, 999);
  const ms = expiryMonthEnd.getTime() - startOfToday.getTime();
  return Math.ceil(ms / 86_400_000);
}

function dayWord(n: number) {
  return Math.abs(n) === 1 ? "day" : "days";
}

function DashMock() {
  const daysRemaining = getDaysUntilMonthEnd(new Date(), DASH_MOCK_EXPIRY_MONTH, DASH_MOCK_EXPIRY_YEAR);
  const isExpired = daysRemaining <= 0;
  const isAtRisk = !isExpired && daysRemaining <= 90;
  const mockLineValue = 1240;

  const writeOffValue = isExpired ? mockLineValue : 0;
  const atRiskValue = isAtRisk ? mockLineValue : 0;
  const recoveredValue = !isExpired && !isAtRisk ? mockLineValue : 0;

  const itemBorder = isExpired ? C.wRed : isAtRisk ? C.wAmber : C.wSafe;
  const badgeLabel = isExpired ? "EXPIRED" : isAtRisk ? "AT RISK" : "SAFE";
  const badgeBg = isExpired ? "rgba(239,68,68,0.14)" : isAtRisk ? "rgba(245,158,11,0.14)" : "rgba(16,185,129,0.12)";
  const badgeColor = isExpired ? C.wRed : isAtRisk ? C.wAmber : C.wSafe;
  const dateLine = isExpired ? "30 tablets · Expired 07/2026" : "30 tablets · Expires 07/2026";
  const dayLine = isExpired
    ? `Overdue by ${Math.abs(daysRemaining)} ${dayWord(daysRemaining)}`
    : `${daysRemaining} ${dayWord(daysRemaining)} remaining`;

  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden",
      boxShadow:"0 24px 80px rgba(27,43,74,0.12),0 4px 20px rgba(27,43,74,0.06)",width:"100%",maxWidth:820,animation:"fadeUp 0.8s 0.4s ease both"}}>
      <div style={{background:C.surfaceMid,borderBottom:`1px solid ${C.border}`,padding:"11px 16px",display:"flex",alignItems:"center",gap:12}}>
        <div style={{display:"flex",gap:6}}>{["#FF5F57","#FEBC2E","#28C840"].map(c=><div key={c} style={{width:11,height:11,borderRadius:"50%",background:c}}/>)}</div>
        <div style={{flex:1,background:"rgba(27,43,74,0.06)",borderRadius:6,padding:"4px 12px",fontSize:12,color:C.muted,textAlign:"center",maxWidth:240,margin:"0 auto"}}>expirydesk.co.za/dashboard</div>
      </div>
      <div style={{padding:20,background:"#EEF5FB"}}>
        <div style={{display:"flex",gap:8,marginBottom:16}}>
          {["Dashboard","How to use"].map((t,i)=><div key={t} style={{padding:"8px 18px",borderRadius:10,fontSize:13,fontWeight:i===0?600:400,color:i===0?C.navy:C.muted,background:i===0?"#fff":"transparent",border:i===0?`1px solid ${C.border}`:"none"}}>{t}</div>)}
        </div>
        <div style={{display:"flex",gap:8,marginBottom:10}}>
          {[["R","Finance desk"],["#","Stock & counts"]].map(([ic,lb],i)=>(
            <div key={lb} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",borderRadius:9,fontSize:13,fontWeight:i===0?600:400,color:i===0?C.teal:C.muted,background:"#fff",border:`1px solid ${i===0?C.teal:C.border}`}}>
              <span style={{fontSize:11,fontWeight:700}}>{ic}</span>{lb}
            </div>
          ))}
        </div>
        <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Rand totals and line values — best for month-end.</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
          {[
            ["WRITE-OFFS", formatZarAmount(writeOffValue), C.wRed, "Written-off stock"],
            ["AT RISK", formatZarAmount(atRiskValue), C.wAmber, "≤90 days, active"],
            ["RECOVERED", formatZarAmount(recoveredValue), C.wSafe, "Moved / dispensed"],
          ].map(([lb,val,col,sub])=>(
            <div key={lb} style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,padding:"14px 16px"}}>
              <div style={{fontSize:10,color:C.muted,letterSpacing:"0.8px",textTransform:"uppercase",marginBottom:6}}>{lb}</div>
              <div style={{fontSize:24,fontWeight:700,color:col,fontFamily:"Georgia,serif",letterSpacing:"-1px"}}>{val}</div>
              <div style={{fontSize:12,color:C.muted,marginTop:4}}>{sub}</div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",gap:8}}>
            {["All","Active","Moved","Expired"].map((f,i)=>(
              <div key={f} style={{padding:"6px 14px",borderRadius:20,fontSize:13,background:i===0?C.teal:"#fff",color:i===0?C.navyDeep:C.muted,border:`1px solid ${i===0?C.teal:C.border}`,fontWeight:i===0?600:400}}>{f}</div>
            ))}
          </div>
          <div style={{fontSize:13,color:C.muted,border:`1px solid ${C.border}`,padding:"6px 12px",borderRadius:20,background:"#fff"}}>Sort · Urgency</div>
        </div>
        <div style={{background:"#fff",border:`2px solid ${itemBorder}`,borderRadius:14,padding:16}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
            <span style={{fontSize:16,fontWeight:700,color:C.navy}}>Norvasc 5mg</span>
            <span style={{fontSize:13,color:C.muted}}> · 5mg</span>
            <div style={{marginLeft:"auto",background:badgeBg,color:badgeColor,fontSize:12,fontWeight:600,padding:"3px 12px",borderRadius:20}}>{badgeLabel}</div>
          </div>
          <div style={{fontSize:13,color:C.muted}}>{dateLine}</div>
          <div style={{fontSize:14,fontWeight:600,color:C.navy,margin:"10px 0"}}>{dayLine}</div>
          <div style={{display:"flex",gap:10}}>
            {["Moved / dispensed","Marked expired"].map(l=>(
              <div key={l} style={{flex:1,padding:"8px 12px",border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,color:C.muted,textAlign:"center"}}>{l}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Viewfinder mood before capture — replace with any marketing plate if you prefer. */
const LANDING_BATCH_DEMO_IMG = "/landing/batch-demo-norvasc.png";
/** User-supplied Norvasc 5mg pack — shown after “capture” on the landing dummy. */
const LANDING_NORVASC_CAPTURED_IMG = "/landing/norvasc-capture-result.png";

type LandingDemoBackdrop = {
  src: string;
  objectFit: "cover" | "contain";
  objectPosition: string;
};

const SCAN_MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** Target year anchor for the demo picker range. */
const DEMO_TARGET_YEAR = 2026;

function getLocalTodayParts() {
  const d = new Date();
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

function landingFormatExpiry(month: number, year: number) {
  return `${String(month).padStart(2, "0")}/${year}`;
}

/** Always includes DEMO_TARGET_YEAR so the Aug/2026 demo path stays reachable. */
function buildDemoYears(anchorYear: number) {
  const lo = Math.min(anchorYear - 4, DEMO_TARGET_YEAR - 2);
  const hi = Math.max(anchorYear + 6, DEMO_TARGET_YEAR + 2);
  const years: number[] = [];
  for (let y = lo; y <= hi; y++) years.push(y);
  return years;
}

function LandingDemoWheelColumn({
  label,
  values,
  value,
  onChange,
  disabled,
  variant,
}: {
  label: string;
  values: number[];
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
  variant: "month" | "year";
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const settleRef = useRef<number | null>(null);
  const scrollMetaRef = useRef({ t: 0, y: 0, v: 0 });
  const itemHeight = 40;
  const padItems = 2;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const idx = values.indexOf(value);
    if (idx < 0) return;
    el.scrollTo({ top: idx * itemHeight, behavior: "smooth" });
  }, [value, values]);

  const onScroll = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const prev = scrollMetaRef.current;
    const dt = Math.max(1, now - prev.t);
    const dy = Math.abs(el.scrollTop - prev.y);
    const v = dy / dt;
    scrollMetaRef.current = { t: now, y: el.scrollTop, v };

    if (settleRef.current) window.clearTimeout(settleRef.current);
    const settleMs = v > 0.85 ? 95 : v > 0.45 ? 78 : 62;
    settleRef.current = window.setTimeout(() => {
      const idx = Math.round(el.scrollTop / itemHeight);
      const next = values[Math.max(0, Math.min(values.length - 1, idx))];
      const targetTop = Math.max(0, Math.min(values.length - 1, idx)) * itemHeight;
      el.scrollTo({ top: targetTop, behavior: "smooth" });
      if (next !== value) onChange(next);
    }, settleMs);
  }, [values, onChange, value]);

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 9,
          color: "rgba(235,235,245,0.55)",
          marginBottom: 4,
          textAlign: "center",
          letterSpacing: "0.4px",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div className="ed-batch-wheel-shell" style={{ height: 200 }}>
        <div
          ref={wrapRef}
          onScroll={onScroll}
          className="ed-wheel-scroll"
          style={{
            height: "100%",
            overflowY: "auto",
            scrollSnapType: "y mandatory",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.8) 16%, rgba(0,0,0,1) 28%, rgba(0,0,0,1) 72%, rgba(0,0,0,0.8) 84%, transparent 100%)",
            paddingTop: itemHeight * padItems,
            paddingBottom: itemHeight * padItems,
            opacity: disabled ? 0.45 : 1,
            pointerEvents: disabled ? "none" : "auto",
          }}
        >
          {values.map((v) => (
            <div
              key={String(v)}
              style={{
                height: itemHeight,
                lineHeight: `${itemHeight}px`,
                textAlign: "center",
                color: v === value ? "#f5f5f7" : "rgba(235,235,245,0.45)",
                fontWeight: v === value ? 700 : 500,
                fontSize: "0.95rem",
                scrollSnapAlign: "center",
              }}
            >
              {variant === "month" ? SCAN_MONTHS_FULL[(v as number) - 1] : v}
            </div>
          ))}
        </div>
        <div
          className="ed-batch-wheel-selection"
          style={{
            left: 8,
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            height: itemHeight,
          }}
        />
      </div>
    </div>
  );
}

function DemoBatchHeader({
  progressPhase,
  confirmedCount,
  onSubmit,
}: {
  progressPhase: 0 | 1 | 2;
  confirmedCount: number;
  onSubmit: () => void;
}) {
  return (
    <div
      style={{
        position: "relative",
        zIndex: 3,
        padding: "6px 10px 8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 6,
      }}
    >
      <div style={{ color: "#f5f5f7", fontSize: 11, fontWeight: 700 }}>Item 1</div>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        {([0, 1, 2] as const).map((i) => (
          <span
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: progressPhase === i ? "#0ea5e9" : "rgba(235,235,245,0.35)",
            }}
          />
        ))}
        <span style={{ color: "rgba(235,235,245,0.6)", fontSize: 9, marginLeft: 2 }}>Confirmed {confirmedCount}</span>
      </div>
      <button type="button" className="ed-scan-done" onClick={onSubmit}>
        Submit
      </button>
    </div>
  );
}

function DemoNorvascBackdrop({ blur = false, backdrop }: { blur?: boolean; backdrop: LandingDemoBackdrop }) {
  const { src, objectFit, objectPosition } = backdrop;
  const isContain = objectFit === "contain";
  return (
    <>
      <Image
        src={src}
        alt=""
        fill
        sizes="(max-width: 768px) 100vw, 400px"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit,
          objectPosition,
          background: isContain ? "#0a0a0b" : "transparent",
          filter: blur ? "blur(10px) brightness(0.45)" : isContain ? "brightness(0.97)" : "brightness(0.55)",
          transform: blur ? "scale(1.06)" : "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, rgba(8, 20, 40, 0.52), transparent 38%, rgba(6, 12, 24, 0.82))",
          pointerEvents: "none",
        }}
      />
    </>
  );
}

type Screen1Props = {
  captured: boolean;
  backdrop: LandingDemoBackdrop;
  onCapture: () => void;
  onRetake: () => void;
  onHeaderSubmit: () => void;
  confirmedCount: number;
  progressPhase: 0 | 1 | 2;
};
type Screen2Props = {
  active: boolean;
  backdrop: LandingDemoBackdrop;
  month: number;
  year: number;
  years: number[];
  dateLocked: boolean;
  autoAdvancing: boolean;
  labelReady: boolean;
  onMonth: (m: number) => void;
  onYear: (y: number) => void;
  onRetake: () => void;
  onHeaderSubmit: () => void;
  confirmedCount: number;
  progressPhase: 0 | 1 | 2;
};
type Screen3Props = {
  active: boolean;
  backdrop: LandingDemoBackdrop;
  month: number;
  year: number;
  productName: string;
  onProductName: (s: string) => void;
  confirmed: boolean;
  onConfirm: () => void;
  onBackToDate: () => void;
  onHeaderSubmit: () => void;
  confirmedCount: number;
  progressPhase: 0 | 1 | 2;
};

// iPhone screen content for each step (interactive landing demo — matches in-app batch scan UI)
function Screen1({ captured, backdrop, onCapture, onRetake, onHeaderSubmit, confirmedCount, progressPhase }: Screen1Props) {
  return (
    <div
      className="batch-scan-ui"
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#000",
        fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
      }}
    >
      <DemoNorvascBackdrop blur={false} backdrop={backdrop} />
      <DemoBatchHeader progressPhase={progressPhase} confirmedCount={confirmedCount} onSubmit={onHeaderSubmit} />
      <div
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "0 14px 52px",
          gap: 12,
        }}
      >
        <div style={{ color: "rgba(235,235,245,0.75)", fontSize: 11, textAlign: "center" }}>Step 1 · Capture medicine label</div>
        <button
          type="button"
          onClick={onCapture}
          className="ed-scan-shutter"
          aria-label="Capture"
          style={{ opacity: captured ? 0.55 : 1 }}
        >
          <span className="ed-scan-shutter-inner" />
        </button>
        <button type="button" onClick={onCapture} className="ed-scan-ghost">
          Upload label photo
        </button>
        <div style={{ color: "rgba(235,235,245,0.5)", fontSize: 8, textAlign: "center", lineHeight: 1.45 }}>
          Pro: 0 of 20 scans used. Need more? Premium is unlimited.
        </div>
      </div>
      {captured ? (
        <button
          type="button"
          onClick={onRetake}
          className="ed-scan-ghost"
          style={{
            position: "absolute",
            bottom: 10,
            left: 10,
            zIndex: 4,
            padding: "7px 12px",
            fontSize: 10,
          }}
        >
          ← Retake
        </button>
      ) : null}
    </div>
  );
}

function Screen2({
  active,
  backdrop,
  month,
  year,
  years,
  dateLocked,
  autoAdvancing,
  labelReady,
  onMonth,
  onYear,
  onRetake,
  onHeaderSubmit,
  confirmedCount,
  progressPhase,
}: Screen2Props) {
  const monthValues = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  return (
    <div
      className="batch-scan-ui"
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#000",
        fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
      }}
    >
      <DemoNorvascBackdrop blur backdrop={backdrop} />
      {!active && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}>Capture a label on the left phone first</span>
        </div>
      )}
      <DemoBatchHeader progressPhase={progressPhase} confirmedCount={confirmedCount} onSubmit={onHeaderSubmit} />
      <div
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          padding: "0 12px 44px",
          gap: 10,
        }}
      >
        <div style={{ color: "rgba(235,235,245,0.75)", fontSize: 11 }}>Step 2 · Set expiry month/year</div>
        <div style={{ color: "rgba(235,235,245,0.48)", fontSize: 9, marginTop: -6, lineHeight: 1.4 }}>
          AI extracts the product name in the background while you select expiry.
        </div>
        <div className="ed-batch-panel" style={{ padding: 10 }}>
          <div style={{ color: "rgba(235,235,245,0.65)", fontSize: 9, marginBottom: 4 }}>Product name</div>
          <div style={{ color: "#f5f5f7", minHeight: 22, display: "flex", alignItems: "center", gap: 8 }}>
            {!active || !labelReady ? (
              <>
                <HourglassLoader size={14} label="Reading label" style={{ filter: "brightness(1.15)" }} />
                <span style={{ color: "rgba(235,235,245,0.8)", fontSize: 10 }}>Reading label text…</span>
              </>
            ) : (
              <span style={{ fontSize: 11 }}>Norvasc 5mg (Tablets)</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <LandingDemoWheelColumn
            label="Month"
            values={monthValues}
            value={month}
            onChange={onMonth}
            disabled={!active || dateLocked}
            variant="month"
          />
          <LandingDemoWheelColumn label="Year" values={years} value={year} onChange={onYear} disabled={!active || dateLocked} variant="year" />
        </div>
        {active && !dateLocked ? (
          <div style={{ color: "rgba(235,235,245,0.5)", fontSize: 8, lineHeight: 1.4 }}>
            Move month and year once each. After you stop scrolling, this demo auto-opens verify.
          </div>
        ) : null}
        {dateLocked ? (
          <div style={{ fontSize: 9, color: "#7dd3fc", fontWeight: 600, textAlign: "center" }}>
            {autoAdvancing ? "Date locked · opening verify step…" : "Date locked · check the right phone"}
          </div>
        ) : null}
        {active && !dateLocked ? (
          <button
            type="button"
            onClick={onRetake}
            className="ed-scan-retry"
            style={{ alignSelf: "flex-start", fontSize: 10, padding: "8px 12px" }}
          >
            ← Retake photo
          </button>
        ) : null}
        <div style={{ fontSize: 8, color: "rgba(255,255,255,0.28)", textAlign: "center" }}>Interactive demo · not your real data</div>
      </div>
    </div>
  );
}

function Screen3({
  active,
  backdrop,
  month,
  year,
  productName,
  onProductName,
  confirmed,
  onConfirm,
  onBackToDate,
  onHeaderSubmit,
  confirmedCount,
  progressPhase,
}: Screen3Props) {
  return (
    <div
      className="batch-scan-ui"
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#000",
        fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
      }}
    >
      <DemoNorvascBackdrop blur backdrop={backdrop} />
      {!active && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}>Lock the expiry on the middle phone first</span>
        </div>
      )}
      <DemoBatchHeader progressPhase={progressPhase} confirmedCount={confirmedCount} onSubmit={onHeaderSubmit} />
      <div
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          padding: "0 12px 44px",
          gap: 10,
        }}
      >
        <div style={{ color: "rgba(235,235,245,0.75)", fontSize: 11 }}>Step 3 · Verify and confirm</div>
        <div className="ed-batch-panel ed-batch-panel--elevated" style={{ padding: 12 }}>
          <div style={{ color: "rgba(235,235,245,0.65)", fontSize: 9, marginBottom: 6, lineHeight: 1.35 }}>
            Verify product name before saving to inventory
          </div>
          <input
            value={productName}
            onChange={(e) => onProductName(e.target.value)}
            disabled={!active || confirmed}
            placeholder="Enter product name"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 10px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.16)",
              background: "rgba(0,0,0,0.3)",
              color: "#fff",
              fontSize: 12,
            }}
          />
          <div style={{ color: "rgba(235,235,245,0.82)", marginTop: 8, fontWeight: 600, fontSize: 11 }}>
            Expiry {landingFormatExpiry(month, year)}
          </div>
        </div>
        {confirmed ? (
          <div
            style={{
              background: "rgba(48, 209, 88, 0.18)",
              border: "1px solid rgba(48, 209, 88, 0.45)",
              borderRadius: 11,
              padding: "11px 0",
              textAlign: "center",
              fontSize: 12,
              fontWeight: 700,
              color: "#d1fadf",
            }}
          >
            Demo saved — next item
          </div>
        ) : (
          <button
            type="button"
            disabled={!active || !productName.trim()}
            onClick={onConfirm}
            className={`ed-scan-primary ed-confirm-btn${active && productName.trim() ? " ed-confirm-btn--ready" : ""}`}
          >
            Confirm line &amp; next item
          </button>
        )}
        <button type="button" disabled={!active || confirmed} onClick={onBackToDate} className="ed-scan-ghost">
          Back to expiry
        </button>
        <div style={{ fontSize: 8, color: "rgba(255,255,255,0.28)", textAlign: "center" }}>Landing preview only</div>
      </div>
      {active && !confirmed ? (
        <button
          type="button"
          onClick={onBackToDate}
          className="ed-scan-ghost"
          style={{
            position: "absolute",
            bottom: 8,
            left: 8,
            zIndex: 5,
            padding: "8px 11px",
            fontSize: 10,
          }}
        >
          ← Expiry
        </button>
      ) : null}
    </div>
  );
}

function LandingScanDemo() {
  const demoRef = useRef<HTMLDivElement | null>(null);
  const [captured, setCaptured] = useState(false);
  const [month, setMonth] = useState(() => getLocalTodayParts().month);
  const [year, setYear] = useState(() => getLocalTodayParts().year);
  const [demoYears, setDemoYears] = useState(() => buildDemoYears(getLocalTodayParts().year));
  const [dateLocked, setDateLocked] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [autoAdvancing, setAutoAdvancing] = useState(false);
  const [monthTouched, setMonthTouched] = useState(false);
  const [yearTouched, setYearTouched] = useState(false);
  const [labelReady, setLabelReady] = useState(false);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [productName, setProductName] = useState("Norvasc 5mg (Tablets)");
  const [settledOnPage, setSettledOnPage] = useState(false);
  const [showTryPrompt, setShowTryPrompt] = useState(false);
  const [exhibitionStage, setExhibitionStage] = useState<"waiting" | "intro" | "zoom" | "guiding" | "done">("waiting");

  const progressPhase = !captured ? 0 : !dateLocked ? 1 : 2;

  const demoBackdrop = useMemo<LandingDemoBackdrop>(
    () =>
      captured
        ? { src: LANDING_NORVASC_CAPTURED_IMG, objectFit: "contain", objectPosition: "center center" }
        : { src: LANDING_BATCH_DEMO_IMG, objectFit: "cover", objectPosition: "center 35%" },
    [captured],
  );

  const snapPickerToToday = useCallback(() => {
    const t = getLocalTodayParts();
    setMonth(t.month);
    setYear(t.year);
    setDemoYears(buildDemoYears(t.year));
  }, []);

  const resetDemo = useCallback(() => {
    setCaptured(false);
    snapPickerToToday();
    setDateLocked(false);
    setConfirmed(false);
    setAutoAdvancing(false);
    setMonthTouched(false);
    setYearTouched(false);
    setLabelReady(false);
    setConfirmedCount(0);
    setProductName("Norvasc 5mg (Tablets)");
  }, [snapPickerToToday]);

  const startGuidedRun = useCallback(() => {
    resetDemo();
    setShowTryPrompt(false);
    setExhibitionStage("guiding");
  }, [resetDemo]);

  const retakePhoto = useCallback(() => {
    setCaptured(false);
    setDateLocked(false);
    setConfirmed(false);
    setAutoAdvancing(false);
    setMonthTouched(false);
    setYearTouched(false);
    setLabelReady(false);
  }, []);

  useEffect(() => {
    if (!captured) {
      const reset = window.setTimeout(() => setLabelReady(false), 0);
      return () => window.clearTimeout(reset);
    }
    const t = window.setTimeout(() => setLabelReady(true), 380);
    return () => window.clearTimeout(t);
  }, [captured]);

  useEffect(() => {
    if (!dateLocked || confirmed) return undefined;
    const arm = window.setTimeout(() => setAutoAdvancing(true), 0);
    const toUnlock = window.setTimeout(() => {
      setAutoAdvancing(false);
    }, 850);
    return () => {
      window.clearTimeout(arm);
      window.clearTimeout(toUnlock);
    };
  }, [dateLocked, confirmed]);

  useEffect(() => {
    if (!captured || dateLocked || confirmed || !monthTouched || !yearTouched) return undefined;
    const toAdvance = window.setTimeout(() => {
      setAutoAdvancing(true);
    }, 0);
    const toLock = window.setTimeout(() => {
      setDateLocked(true);
    }, 420);
    return () => {
      window.clearTimeout(toAdvance);
      window.clearTimeout(toLock);
    };
  }, [captured, dateLocked, confirmed, monthTouched, yearTouched, month, year]);

  useEffect(() => {
    if (!confirmed) return undefined;
    if (exhibitionStage === "guiding") {
      const done = window.setTimeout(() => {
        setExhibitionStage("done");
        setShowTryPrompt(true);
      }, 700);
      return () => window.clearTimeout(done);
    }
    const toLoop = window.setTimeout(() => {
      resetDemo();
    }, 580);
    return () => window.clearTimeout(toLoop);
  }, [confirmed, exhibitionStage, resetDemo]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const node = demoRef.current;
    if (!node) return undefined;
    let settleTimer: number | null = null;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || settledOnPage) return;
        settleTimer = window.setTimeout(() => {
          setSettledOnPage(true);
          setExhibitionStage("intro");
        }, 680);
      },
      { threshold: 0.55 },
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
      if (settleTimer != null) window.clearTimeout(settleTimer);
    };
  }, [settledOnPage]);

  useEffect(() => {
    if (exhibitionStage !== "intro") return undefined;
    const toZoom = window.setTimeout(() => setExhibitionStage("zoom"), 1100);
    return () => window.clearTimeout(toZoom);
  }, [exhibitionStage]);

  useEffect(() => {
    if (exhibitionStage !== "zoom") return undefined;
    const toGuide = window.setTimeout(() => startGuidedRun(), 760);
    return () => window.clearTimeout(toGuide);
  }, [exhibitionStage, startGuidedRun]);

  useEffect(() => {
    if (exhibitionStage !== "guiding") return undefined;
    if (!captured) {
      const t = window.setTimeout(() => {
        snapPickerToToday();
        setCaptured(true);
        setConfirmed(false);
        setAutoAdvancing(false);
        setMonthTouched(false);
        setYearTouched(false);
      }, 340);
      return () => window.clearTimeout(t);
    }
    if (captured && !dateLocked && !monthTouched && !yearTouched) {
      const t = window.setTimeout(() => {
        const nextMonth = month === 12 ? 11 : month + 1;
        setMonth(nextMonth);
        setMonthTouched(true);
        setYear(year);
        setYearTouched(true);
      }, 360);
      return () => window.clearTimeout(t);
    }
    if (captured && dateLocked && !confirmed) {
      const t = window.setTimeout(() => {
        setConfirmed(true);
        setConfirmedCount((c) => c + 1);
      }, 650);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [captured, confirmed, dateLocked, exhibitionStage, month, snapPickerToToday, year, monthTouched, yearTouched]);

  return (
    <div ref={demoRef} style={{ position: "relative" }}>
      {(exhibitionStage === "waiting" || exhibitionStage === "intro" || exhibitionStage === "zoom") && (
        <Reveal
          style={{
            padding: "18px 10px 12px",
            borderRadius: 20,
            border: `1px solid ${C.border}`,
            background: "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(238,245,251,0.95) 100%)",
            marginBottom: 16,
            transform: exhibitionStage === "zoom" ? "scale(0.935) translateY(-6px)" : "scale(1) translateY(0)",
            transition: "transform 0.62s cubic-bezier(0.22,1,0.36,1), opacity 0.62s ease",
            opacity: exhibitionStage === "waiting" ? 0.55 : 1,
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: "0.08em", color: C.teal, fontWeight: 700, textTransform: "uppercase", textAlign: "center", marginBottom: 10 }}>
            {exhibitionStage === "waiting" ? "Preparing live walkthrough…" : "Quick exhibition"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 18, alignItems: "center" }}>
            <div
              style={{
                borderRadius: 16,
                border: `1px solid ${C.border}`,
                background: "linear-gradient(180deg,#ffffff 0%,#f7fbff 100%)",
                padding: "14px 16px",
                minHeight: 190,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(115deg, rgba(255,255,255,0) 0%, rgba(29,184,196,0.10) 35%, rgba(255,255,255,0) 70%)",
                  transform: exhibitionStage === "zoom" ? "translateX(25%)" : "translateX(-55%)",
                  transition: "transform 1.1s ease",
                  pointerEvents: "none",
                }}
              />
              <div style={{ fontSize: 12, color: C.navy, fontWeight: 700, marginBottom: 8 }}>Pharmacy shelf context</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0,1fr))", gap: 7 }}>
                {Array.from({ length: 18 }).map((_, i) => (
                  <div
                    key={`shelf-${i}`}
                    style={{
                      height: i % 6 === 0 ? 30 : 24,
                      borderRadius: 6,
                      background:
                        i % 4 === 0
                          ? "linear-gradient(180deg, rgba(29,184,196,0.24), rgba(29,184,196,0.14))"
                          : i % 3 === 0
                            ? "linear-gradient(180deg, rgba(27,43,74,0.20), rgba(27,43,74,0.12))"
                            : "linear-gradient(180deg, rgba(255,255,255,0.9), rgba(231,241,249,0.9))",
                      border: "1px solid rgba(27,43,74,0.08)",
                    }}
                  />
                ))}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
                We start wide with shelf context, then zoom into live capture → expiry set → verify.
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                style={{
                  transform:
                    exhibitionStage === "zoom"
                      ? "perspective(900px) rotateY(-10deg) rotateZ(-1deg) translateX(-8px)"
                      : "perspective(900px) rotateY(-18deg) rotateZ(-2deg)",
                  transition: "transform 0.68s cubic-bezier(0.22,1,0.36,1)",
                }}
              >
                <IPhone17Pro
                  label="Demo perspective"
                  screen={
                    <Screen1
                      captured={false}
                      backdrop={{ src: LANDING_BATCH_DEMO_IMG, objectFit: "cover", objectPosition: "center 35%" }}
                      onCapture={() => {}}
                      onRetake={() => {}}
                      onHeaderSubmit={() => {}}
                      confirmedCount={0}
                      progressPhase={0}
                    />
                  }
                />
              </div>
            </div>
          </div>
        </Reveal>
      )}

      {(exhibitionStage === "guiding" || exhibitionStage === "done") && (
        <ScaleGroup gap={20}>
          <IPhone17Pro
            label="Step 1 · Capture"
            screen={
              <Screen1
                captured={captured}
                backdrop={demoBackdrop}
                onCapture={() => {
                  snapPickerToToday();
                  setCaptured(true);
                  setConfirmed(false);
                  setAutoAdvancing(false);
                  setMonthTouched(false);
                  setYearTouched(false);
                }}
                onRetake={retakePhoto}
                onHeaderSubmit={resetDemo}
                confirmedCount={confirmedCount}
                progressPhase={progressPhase}
              />
            }
          />
          <div style={{fontSize:26,color:C.teal,opacity:0.4,marginTop:160,flexShrink:0}}>→</div>
          <IPhone17Pro
            label="Step 2 · Set expiry"
            screen={
              <Screen2
                active={captured}
                backdrop={demoBackdrop}
                month={month}
                year={year}
                years={demoYears}
                dateLocked={dateLocked}
                autoAdvancing={autoAdvancing}
                labelReady={labelReady}
                onMonth={(nextMonth) => {
                  setMonth(nextMonth);
                  setMonthTouched(true);
                }}
                onYear={(nextYear) => {
                  setYear(nextYear);
                  setYearTouched(true);
                }}
                onRetake={retakePhoto}
                onHeaderSubmit={resetDemo}
                confirmedCount={confirmedCount}
                progressPhase={progressPhase}
              />
            }
          />
          <div style={{fontSize:26,color:C.teal,opacity:0.4,marginTop:160,flexShrink:0}}>→</div>
          <IPhone17Pro
            label="Step 3 · Verify"
            screen={
              <Screen3
                active={dateLocked}
                backdrop={demoBackdrop}
                month={month}
                year={year}
                productName={productName}
                onProductName={setProductName}
                confirmed={confirmed}
                onConfirm={() => {
                  setConfirmed(true);
                  setConfirmedCount((c) => c + 1);
                }}
                onBackToDate={() => {
                  setDateLocked(false);
                  setConfirmed(false);
                  setAutoAdvancing(false);
                  setMonthTouched(false);
                  setYearTouched(false);
                }}
                onHeaderSubmit={resetDemo}
                confirmedCount={confirmedCount}
                progressPhase={progressPhase}
              />
            }
          />
        </ScaleGroup>
      )}

      {showTryPrompt && (
        <Reveal
          style={{
            marginTop: 18,
            borderRadius: 16,
            border: "1px solid rgba(29,184,196,0.30)",
            background: "rgba(29,184,196,0.09)",
            padding: "16px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 14,
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Walkthrough complete — now try it yourself</div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              Tap capture on the left phone and run the flow manually.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              resetDemo();
              setShowTryPrompt(false);
              setExhibitionStage("done");
            }}
            style={{
              border: "none",
              borderRadius: 999,
              background: C.teal,
              color: C.navyDeep,
              fontWeight: 700,
              fontSize: 12,
              padding: "10px 16px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Try the demo
          </button>
        </Reveal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
export function LandingPage() {
  const [isMobileNav, setIsMobileNav] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCompactLandscape, setIsCompactLandscape] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(max-width: 960px)");
    const update = () => setIsMobileNav(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-height: 460px) and (orientation: landscape)");
    const update = () => setIsCompactLandscape(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (isMobileNav) return undefined;
    const id = requestAnimationFrame(() => setMobileMenuOpen(false));
    return () => cancelAnimationFrame(id);
  }, [isMobileNav]);

  useEffect(() => {
    if (!isMobileNav || !mobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileNav, mobileMenuOpen]);

  /** Full wordmark in nav — ~3–4× prior 56px height so it matches nav / CTA weight. */
  const navLogoHeight = isMobileNav ? (isCompactLandscape ? 38 : 52) : 200;
  const navVPx = isMobileNav ? (isCompactLandscape ? 6 : 10) : 16;
  const navPadding = `max(${navVPx}px, env(safe-area-inset-top)) max(5%, env(safe-area-inset-right)) max(${navVPx}px, env(safe-area-inset-bottom)) max(5%, env(safe-area-inset-left))`;
  const heroMinHeight = isMobileNav ? "min(100dvh, 100svh)" : "100vh";
  const heroPadding = isMobileNav
    ? `max(${isCompactLandscape ? 28 : 36}px, env(safe-area-inset-top)) max(5%, env(safe-area-inset-right)) ${isCompactLandscape ? 44 : 56}px max(5%, env(safe-area-inset-left))`
    : `max(48px, env(safe-area-inset-top)) max(5%, env(safe-area-inset-right)) 80px max(5%, env(safe-area-inset-left))`;

  return (
    <div style={{fontFamily:"DM Sans,system-ui,sans-serif",background:C.surface,color:C.navy,overflowX:"hidden",position:"relative"}}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.35}}
        @keyframes ctaPulse{0%,100%{transform:translateY(0) scale(1);box-shadow:0 4px 24px rgba(29,184,196,0.22)}50%{transform:translateY(-1px) scale(1.035);box-shadow:0 10px 30px rgba(29,184,196,0.32)}}
        @keyframes pulsePin{0%,100%{transform:scale(1);opacity:0.7}50%{transform:scale(1.6);opacity:0}}
        @keyframes hourglassOrbit{from{transform:rotate(0deg) translateX(var(--hourglass-r, 12px)) rotate(0deg)}to{transform:rotate(360deg) translateX(var(--hourglass-r, 12px)) rotate(-360deg)}}
        @keyframes hourglassSpinSmooth{to{transform:rotate(360deg)}}
        @keyframes scanLine{0%,100%{top:25%;opacity:0}20%{opacity:1}50%{top:68%;opacity:1}80%{opacity:1}}
        @keyframes gradShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
        *{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        em{font-style:italic;color:${C.teal};}
        ::selection{background:${C.teal};color:${C.navyDeep};}
        .ed-focusable:focus-visible{outline:2px solid ${C.teal};outline-offset:3px;border-radius:8px;}
        .ed-hero-ctas{display:flex;flex-wrap:wrap;gap:14px;margin-bottom:72px;justify-content:center}
        @media (max-width:960px){
          .ed-hero-ctas{flex-direction:column;align-items:stretch;width:100%;max-width:360px;margin-left:auto;margin-right:auto}
          .ed-hero-ctas > a{text-align:center;justify-content:center}
        }
        @media (prefers-reduced-motion: reduce){
          html{scroll-behavior:auto;}
          *, *::before, *::after{
            animation:none !important;
            transition:none !important;
          }
        }
      `}</style>

      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          minHeight: "100%",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <LandingHourglassField />
      </div>

      {/* ── NAV (scrolls with page — not fixed/sticky) ── */}
      {isMobileNav && mobileMenuOpen ? (
        <button
          type="button"
          aria-label="Close menu"
          className="ed-focusable"
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 90,
            margin: 0,
            padding: 0,
            border: "none",
            background: "rgba(15,23,42,0.22)",
            cursor: "pointer",
          }}
        />
      ) : null}
      <div style={{position:"relative",zIndex:100}}>
        <nav style={{position:"relative",padding:navPadding,display:"flex",alignItems:"center",justifyContent:"space-between",background:"rgba(255,255,255,0.95)",backdropFilter:"blur(18px)",borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:"flex",alignItems:"center",flexShrink:0,maxWidth:isMobileNav?"72%":"min(560px,52vw)"}}>
          <Link href="/" className="ed-focusable" style={{ display: "flex", alignItems: "center", lineHeight: 0 }}>
            <Image
              src="/brand/logo-full.png"
              alt="ExpiryDesk"
              width={640}
              height={120}
              style={{
                height: navLogoHeight,
                width: "auto",
                maxWidth: "100%",
                display: "block",
                objectFit: "contain",
              }}
            />
          </Link>
        </div>
        {isMobileNav ? (
          <button
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            className="ed-focusable"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"8px 12px",fontSize:16,cursor:"pointer",lineHeight:1,minWidth:44,minHeight:44,display:"inline-flex",alignItems:"center",justifyContent:"center",touchAction:"manipulation"}}
          >
            {mobileMenuOpen ? "✕" : "☰"}
          </button>
        ) : (
          <>
            <div style={{display:"flex",gap:32}}>
              {NAV_ITEMS.map((item)=>(
                <a key={item.label} href={item.href} className="ed-focusable" style={navTextLinkStyle} {...withHoverColor(C.muted)}>{item.label}</a>
              ))}
            </div>
            <div style={{display:"flex",gap:12}}>
              <Link href="/dashboard" className="ed-focusable" style={{...navTextLinkStyle,fontFamily:"inherit",padding:"8px 16px",display:"inline-flex",alignItems:"center"}}>Sign In</Link>
              <Link href="/signup" className="ed-focusable" style={{...primarySignupCtaBaseStyle,fontSize:14,fontWeight:600,padding:"9px 20px",borderRadius:9,boxShadow:`0 2px 14px ${C.tealGlow}`}}>Get Started →</Link>
            </div>
          </>
        )}
        </nav>

        {isMobileNav && mobileMenuOpen && (
        <div style={{position:"absolute",left:0,right:0,top:"100%",background:"#ffffff",borderBottom:`1px solid ${C.border}`,padding:"14px max(5%, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(5%, env(safe-area-inset-left))",display:"flex",flexDirection:"column",gap:10,boxShadow:"0 12px 28px rgba(27,43,74,0.12)"}}>
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="ed-focusable"
              onClick={() => setMobileMenuOpen(false)}
              style={{...navTextLinkStyle,fontSize:15,padding:"10px 8px",borderRadius:8}}
              {...withHoverColor(C.muted)}
            >
              {item.label}
            </a>
          ))}
          <div style={{display:"flex",gap:10,marginTop:4,flexWrap:"wrap"}}>
            <Link href="/dashboard" className="ed-focusable" onClick={() => setMobileMenuOpen(false)} style={{...navTextLinkStyle,fontFamily:"inherit",padding:"10px 12px",display:"inline-flex",alignItems:"center",border:`1px solid ${C.border}`,borderRadius:8}}>Sign In</Link>
            <Link href="/signup" className="ed-focusable" onClick={() => setMobileMenuOpen(false)} style={{...primarySignupCtaBaseStyle,fontSize:14,fontWeight:600,padding:"10px 14px",borderRadius:9,boxShadow:`0 2px 14px ${C.tealGlow}`}}>Get Started →</Link>
          </div>
        </div>
        )}
      </div>

      <main id="main" tabIndex={-1} style={{ outline: "none" }}>
      {/* ── HERO ── */}
      <section
        style={{minHeight:heroMinHeight,background:"linear-gradient(168deg,#ffffff 0%,#EEF5FB 55%,#DFF0F7 100%)",position:"relative",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:heroPadding,overflow:"hidden"}}
      >
        <div style={{position:"absolute",top:"-10%",left:"50%",transform:"translateX(-50%)",width:900,height:500,background:"radial-gradient(ellipse at center,rgba(29,184,196,0.07) 0%,transparent 70%)",pointerEvents:"none"}}/>
        <div style={{position:"absolute",inset:0,backgroundImage:`linear-gradient(rgba(29,184,196,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(29,184,196,0.025) 1px,transparent 1px)`,backgroundSize:"60px 60px",pointerEvents:"none"}}/>

        {/* Headline word by word */}
        <SplitWords 
          text="Never lose stock to expired dates again"
          as="h1"
          style={{justifyContent:"center",maxWidth:860,marginBottom:24}}
          wStyle={{fontFamily:"Georgia,serif",fontSize:"clamp(40px,6vw,74px)",fontWeight:700,color:C.navy,lineHeight:1.14,letterSpacing:"-1.6px"}}
          delay={0.15}
        />

        <Reveal delay={0.65}>
          <p style={{fontSize:18,color:C.muted,maxWidth:520,textAlign:"center",lineHeight:1.7,fontWeight:300,marginBottom:40}}>
            Scan stock sheets or product labels. ExpiryDesk tracks expiry on a colour-coded dashboard and alerts you before write-offs — built for pharmacy, retail, and FMCG teams.
          </p>
        </Reveal>
        <Reveal delay={0.8}>
          <div className="ed-hero-ctas">
            <Link href="/signup" className="ed-focusable" style={{...primarySignupCtaBaseStyle,fontSize:16,fontWeight:600,padding:"14px 28px",borderRadius:11,boxShadow:`0 4px 20px ${C.tealGlow}`}}>Get Started →</Link>
            <a href="#how-it-works" className="ed-focusable" style={{background:"#fff",border:`1.5px solid ${C.border}`,color:C.navy,cursor:"pointer",fontSize:16,fontFamily:"inherit",padding:"14px 28px",borderRadius:11,boxShadow:"0 2px 8px rgba(27,43,74,0.06)",textDecoration:"none",display:"inline-flex",alignItems:"center"}}>See How It Works</a>
          </div>
        </Reveal>
        <DashMock/>
      </section>

      {/* ── FEATURE CARDS with ScaleGroup ── */}
      <section id="features" style={{background:C.surface,padding:"48px 5% 96px"}}>
        <Reveal style={{textAlign:"center",maxWidth:900,margin:"0 auto 28px"}}>
          <h2 style={{fontFamily:"Georgia,serif",fontSize:"clamp(28px,4vw,46px)",fontWeight:700,letterSpacing:"-1.2px",color:C.navy,margin:"0 0 10px",lineHeight:1.14}}>
            Features that remove manual pharmacy work
          </h2>
          <p style={{fontSize:16,color:C.muted,lineHeight:1.7,margin:0}}>
            Real-time scanning, instant inventory updates, AI-assisted pill counting, and proactive email insight reports — built to move stock decisions faster than paperwork.
          </p>
        </Reveal>
        <ScaleGroup gap={24} horizontal={!isMobileNav}>
          {[["⚡","Real-time item scanning","Scan product labels in seconds and capture expiry, medicine name, dosage, and dosage form without typing. Built for shelf-speed intake from camera to record.","↓ Less admin time per line item"],
            ["📦","Automatic dashboard + inventory sync","Every confirmed scan updates your dashboard and inventory view immediately, so teams see the same urgency colours, expiry windows, and movement status in real time.","↓ Fewer missed or duplicated entries"],
            ["🔬","AI pill counting with live visual assist","Use advanced vision to detect pills and assist count verification instead of manually counting large batches. Final count still stays human-confirmed for audit confidence.","↓ Faster counts on high-volume stock"],
            ["📬","Email notifications with actionable insights","Get detailed inventory and expiry insights by email, including risk trends and priorities that help managers act before write-offs hit margin.","↓ Better decisions before stock loss"],
          ].map(([ic,ti,de,st])=>(
            <TiltCard key={ti} style={{height:"100%"}}>
              <div style={problemCardStyle}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${C.teal},${C.tealDim})`,borderRadius:"18px 18px 0 0"}}/>
                <div style={{width:52,height:52,borderRadius:14,background:C.tealSoft,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:20,fontSize:24}}>{ic}</div>
                <div style={{fontSize:17,fontWeight:600,color:C.navy,marginBottom:10}}>{ti}</div>
                <p style={{fontSize:14,color:C.muted,lineHeight:1.65}}>{de}</p>
                <div style={{marginTop:20,paddingTop:18,borderTop:`1px solid ${C.border}`,fontSize:13,color:C.teal,fontWeight:500}}>{st}</div>
              </div>
            </TiltCard>
          ))}
        </ScaleGroup>
      </section>

      {/* ── CINEMATIC — scan lead-in ── */}
      <CinematicStatement
        id="point-tap-done"
        lines={["Point. Tap. Done."]}
        sub={"Any product. Any shelf. The label is read, the date is set.\nFOLLOW THE MAGIC IN YOUR DASHBOARD."}
        bg={C.surfaceMid} align="left"/>

      {/* ── SCAN SECTION — iPhone 17 Pro mockups ── */}
      <SectionBlock background={C.card} padding="48px 5% 96px">
        <LandingScanDemo />
      </SectionBlock>

      {/* ── HOW IT WORKS — ScaleGroup on steps (after phone mockups) ── */}
      <section id="how-it-works" style={{background:C.navyDeep,padding:"96px 5%",position:"relative",overflow:"hidden"}}>
        <OrbitingHourglass
          size={420}
          color="rgba(29,184,196,0.45)"
          orbitPx={26}
          orbitSec={76}
          spinSec={58}
          delaySec={1.2}
          style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)", opacity: 0.045 }}
        />
        <div style={{position:"absolute",bottom:-200,right:-200,width:600,height:600,background:"radial-gradient(ellipse,rgba(29,184,196,0.12) 0%,transparent 70%)",pointerEvents:"none"}}/>
        <SplitWords text="Three steps. Zero expired stock."
          style={{justifyContent:"center",marginBottom:64}}
          wStyle={{fontFamily:"Georgia,serif",fontSize:"clamp(28px,4vw,50px)",fontWeight:700,color:"#fff",letterSpacing:"-1.2px",lineHeight:1.15}}
          delay={0}/>
        <div style={{position:"relative"}}>
          <div style={{position:"absolute",top:40,left:"calc(16.66% + 20px)",right:"calc(16.66% + 20px)",height:1,background:"repeating-linear-gradient(90deg,rgba(29,184,196,0.35) 0,rgba(29,184,196,0.35) 8px,transparent 8px,transparent 16px)"}}/>
          <ScaleGroup gap={0} horizontal={!isMobileNav}>
            {[["01","Scan","Point your phone at any product label. Capture it in under 3 seconds. No forms. No typing. No clipboards.","Anthropic Vision"],
              ["02","Track","Your dashboard lights up with urgency colours. Days to expiry visible at a glance. Rand value calculated automatically.","Live Dashboard"],
              ["03","Act","Automated alerts before items expire. Mark stock as sold, moved, or written off in one tap. Every rand is logged.","Automated Alerts"]
            ].map(([num,ti,de,tag])=>(
              <Reveal key={num} style={{padding:"0 40px"}}>
                <div style={{width:80,height:80,borderRadius:"50%",background:"rgba(29,184,196,0.08)",border:"1px solid rgba(29,184,196,0.3)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:28,position:"relative",zIndex:1}}>
                  <span style={{fontFamily:"Georgia,serif",fontSize:28,fontWeight:700,color:C.teal}}>{num}</span>
                </div>
                <div style={{fontSize:22,fontWeight:600,color:"#fff",marginBottom:12,letterSpacing:"-0.5px"}}>{ti}</div>
                <p style={{fontSize:15,color:"rgba(255,255,255,0.42)",lineHeight:1.7}}>{de}</p>
                <div style={{display:"inline-block",marginTop:16,fontSize:11,fontWeight:600,letterSpacing:"0.5px",background:"rgba(29,184,196,0.1)",color:C.teal,padding:"4px 12px",borderRadius:100,border:"1px solid rgba(29,184,196,0.2)"}}>{tag}</div>
              </Reveal>
            ))}
          </ScaleGroup>
        </div>
      </section>

      {/* ── PRICING ── */}
      <SectionBlock id="pricing" background={C.surface} padding="96px 5% 112px">
        <Reveal style={{ textAlign: "center", marginBottom: 48 }}>
          <h2
            style={{
              fontFamily: "Georgia,serif",
              fontSize: "clamp(28px,4vw,46px)",
              fontWeight: 700,
              letterSpacing: "-1.2px",
              color: C.navy,
              marginBottom: 14,
              lineHeight: 1.12,
            }}
          >
            Pay for the work that actually moves stock
          </h2>
          <p style={{ fontSize: 17, color: C.muted, lineHeight: 1.65, maxWidth: 580, margin: "0 auto" }}>
            Try it properly first — then pick the edition that matches your footprint. Straightforward numbers: you are not buying a slide deck, you are buying fewer surprises on the shelf.
          </p>
        </Reveal>
        <div
          aria-label="Pricing editions"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
            gap: 22,
            maxWidth: 1180,
            margin: "0 auto",
            alignItems: "stretch",
          }}
        >
          {/* Staff */}
          <TiltCard>
            <div style={{ ...pricingCardStyle(false), display: "flex", flexDirection: "column", minHeight: "100%" }}>
              <h3 style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.muted, marginBottom: 8, marginTop: 0 }}>
                Staff edition
              </h3>
              <div style={{ fontFamily: "Georgia,serif", fontSize: 40, fontWeight: 700, color: C.navy, letterSpacing: "-1.5px", lineHeight: 1.05, marginBottom: 6 }}>
                Free
              </div>
              <div style={{ fontSize: 13, color: C.teal, fontWeight: 600, marginBottom: 14 }}>Solo tracking · room to learn the muscle memory</div>
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, marginBottom: 18, paddingBottom: 18, borderBottom: `1px solid ${C.border}`, flex: 1 }}>
                Learn the flow on your owner&apos;s profile before the busy day asks for speed. Quiet practice — then confidence when the queue thickens.
              </p>
              <LandingFeatureChecklist features={staffTierFeatures} checkVariant="teal" />
              <Link
                href="/signup"
                className="ed-focusable"
                style={{
                  width: "100%",
                  padding: "13px 14px",
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  background: "#fff",
                  border: `1.5px solid ${C.border}`,
                  color: C.navy,
                  textDecoration: "none",
                  display: "block",
                  textAlign: "center",
                  boxSizing: "border-box",
                  marginTop: "auto",
                }}
              >
                Start free
              </Link>
            </div>
          </TiltCard>

          {/* Manager */}
          <TiltCard>
            <div style={{ ...pricingCardStyle(true), display: "flex", flexDirection: "column", minHeight: "100%", position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  top: -13,
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: C.teal,
                  color: C.navyDeep,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  padding: "5px 14px",
                  borderRadius: 100,
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                Natural step for branch & storeroom leads
              </div>
              <h3 style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.muted, marginBottom: 8, marginTop: 8 }}>
                Manager edition
              </h3>
              <div style={{ fontFamily: "Georgia,serif", fontSize: 40, fontWeight: 700, color: C.navy, letterSpacing: "-1.5px", lineHeight: 1.05, marginBottom: 4 }}>
                {formatZarAmount(MANAGER_MONTHLY_ZAR)}
                <span style={{ fontSize: 15, color: C.muted, fontWeight: 500 }}> / month</span>
              </div>
              <SavingsSubline
                yearlyZar={MANAGER_YEARLY_ZAR}
                save={managerAnnualSave}
                mutedColor={C.muted}
                tealColor={C.teal}
                navyColor={C.navy}
              />
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, marginBottom: 18, paddingBottom: 18, borderBottom: `1px solid ${C.border}`, flex: 1 }}>
                When stock splits across two doors or two storerooms, the question is always the same: where is the truth? Manager keeps one trail — so audits and handovers stay boring in the right way.
              </p>
              <LandingFeatureChecklist features={managerTierFeatures} checkVariant="navy" />
              <Link
                href="/pricing"
                className="ed-focusable"
                style={{
                  width: "100%",
                  padding: "13px 14px",
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  background: C.teal,
                  border: "none",
                  color: C.navyDeep,
                  boxShadow: `0 4px 18px ${C.tealGlow}`,
                  textDecoration: "none",
                  display: "block",
                  textAlign: "center",
                  boxSizing: "border-box",
                  marginTop: "auto",
                }}
              >
                Continue with Manager
              </Link>
            </div>
          </TiltCard>

          {/* Owner */}
          <TiltCard>
            <div style={{ ...pricingCardStyle(false), display: "flex", flexDirection: "column", minHeight: "100%" }}>
              <h3 style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.muted, marginBottom: 8, marginTop: 0 }}>
                Owner edition
              </h3>
              <div style={{ fontFamily: "Georgia,serif", fontSize: 40, fontWeight: 700, color: C.navy, letterSpacing: "-1.5px", lineHeight: 1.05, marginBottom: 4 }}>
                {formatZarAmount(OWNER_MONTHLY_ZAR)}
                <span style={{ fontSize: 15, color: C.muted, fontWeight: 500 }}> / month</span>
              </div>
              <SavingsSubline
                yearlyZar={OWNER_YEARLY_ZAR}
                save={ownerAnnualSave}
                mutedColor={C.muted}
                tealColor={C.teal}
                navyColor={C.navy}
              />
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, marginBottom: 18, paddingBottom: 18, borderBottom: `1px solid ${C.border}`, flex: 1 }}>
                For owners who want every branch to answer in one breath — insight and notifications tied to real lines, not another dashboard nobody opens after week two.
              </p>
              <LandingFeatureChecklist features={ownerTierFeatures} checkVariant="teal" />
              <Link
                href="/pricing"
                className="ed-focusable"
                style={{
                  width: "100%",
                  padding: "13px 14px",
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  background: C.navyDeep,
                  border: "none",
                  color: "#fff",
                  textDecoration: "none",
                  display: "block",
                  textAlign: "center",
                  boxSizing: "border-box",
                  marginTop: "auto",
                }}
              >
                Continue with Owner
              </Link>
            </div>
          </TiltCard>
        </div>
        <p style={{ textAlign: "center", fontSize: 12, color: C.muted, marginTop: 28, maxWidth: 720, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 }}>
          {PRICING_FOOTNOTE_RENEWAL}
        </p>
      </SectionBlock>

      {/* ── TESTIMONIAL with Thabo photo ── */}
      <SectionBlock background={C.surface} padding="100px 5%" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:60,position:"relative",overflow:"hidden",flexWrap:"wrap"}}>
        <Reveal style={{flex:"0 0 auto"}}>
          <ThaboPhoto />
        </Reveal>
        <div style={{flex:1, minWidth: 320, maxWidth: 640}}>
          <Reveal style={{display:"flex",gap:5,marginBottom:20}}>
            {[1,2,3,4,5].map(i=><span key={i} style={{color:C.teal,fontSize:22}}>★</span>)}
          </Reveal>
          
          <Reveal delay={0.1}>
            <div style={{background:"#fff", border:`1px solid ${C.border}`, borderRadius:16, padding:32, boxShadow:"0 12px 40px rgba(27,43,74,0.06)"}}>
              <div style={{fontSize:16, color:C.navy, lineHeight:1.6, marginBottom:20}}>
                <strong style={{color:C.teal}}>The Past:</strong> "We relied on clipboards and memory. Expired stock was only discovered when it was too late to save the margin."
              </div>
              <div style={{fontSize:16, color:C.navy, lineHeight:1.6, marginBottom:20}}>
                <strong style={{color:C.teal}}>The Present:</strong> "ExpiryDesk completely changed our routine. A quick scan logs everything, and the dashboard alerts us to high-risk items automatically."
              </div>
              <div style={{fontSize:16, color:C.navy, lineHeight:1.6, marginBottom:24}}>
                <strong style={{color:C.teal}}>The Future:</strong> "We're expanding. Because ExpiryDesk gives us a real-time view across the dispensary, we can finally scale without losing control of inventory waste."
              </div>
              
              <div style={{display:"flex", alignItems:"center", gap:20, marginBottom:24}}>
                <div style={{background:C.tealSoft, padding:"10px 16px", borderRadius:12}}>
                  <div style={{fontSize:24, fontWeight:700, color:C.teal, fontFamily:"Georgia,serif"}}>60%</div>
                  <div style={{fontSize:12, color:C.navyDeep, fontWeight:600}}>Drop in write-offs</div>
                </div>
                <div style={{background:C.tealSoft, padding:"10px 16px", borderRadius:12}}>
                  <div style={{fontSize:24, fontWeight:700, color:C.teal, fontFamily:"Georgia,serif"}}>2 Days</div>
                  <div style={{fontSize:12, color:C.navyDeep, fontWeight:600}}>Saved per month</div>
                </div>
              </div>

              <div style={{borderTop:`1px solid ${C.border}`, paddingTop:20, display:"flex", alignItems:"center", gap:12}}>
                <div style={{fontWeight:700,fontSize:18,color:C.navy}}>Ms Molefe</div>
                <div style={{fontSize:14,color:C.muted}}>Pharmacy Manager · MedCity</div>
              </div>
            </div>
          </Reveal>
        </div>
      </SectionBlock>

      {/* ── CTA ── */}
      <SectionBlock background={C.surfaceMid} padding="100px 5%" style={{textAlign:"center",position:"relative",overflow:"hidden",borderTop:`1px solid ${C.border}`}}>
        <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:700,height:400,background:"radial-gradient(ellipse,rgba(29,184,196,0.1) 0%,transparent 70%)",pointerEvents:"none"}}/>
        <OrbitingHourglass
          size={110}
          color="rgba(29,184,196,0.5)"
          orbitPx={18}
          orbitSec={56}
          spinSec={42}
          delaySec={0}
          style={{ left: "5%", top: "50%", transform: "translateY(-50%)", opacity: 0.09 }}
        />
        <OrbitingHourglass
          size={72}
          color="rgba(29,184,196,0.48)"
          orbitPx={14}
          orbitSec={62}
          spinSec={46}
          delaySec={1.1}
          style={{ right: "5%", left: "auto", top: "50%", transform: "translateY(-50%)", opacity: 0.07 }}
        />
        <SplitWords text="Ready to stop losing stock?"
          style={{justifyContent:"center",marginBottom:16}}
          wStyle={{fontFamily:"Georgia,serif",fontSize:"clamp(28px,4vw,50px)",fontWeight:700,color:C.navy,letterSpacing:"-1.2px",lineHeight:1.15}}/>
        <Reveal delay={0.4}>
          <div style={{maxWidth:460,margin:"0 auto 36px"}}>
            <p style={{fontSize:17,color:C.muted,maxWidth:440,margin:"0 auto",lineHeight:1.65}}>
              Join inventory-led teams across South Africa using ExpiryDesk to cut expiry waste and protect margin.
            </p>
          </div>
          <Link
            href="/signup"
            className="ed-focusable"
            style={{...primarySignupCtaBaseStyle,gap:10,fontSize:16,fontWeight:700,padding:"15px 34px",borderRadius:12,boxShadow:`0 4px 24px ${C.tealGlow}`,animation:"ctaPulse 2.4s ease-in-out infinite",transition:"transform 0.22s cubic-bezier(0.22,1,0.36,1), box-shadow 0.22s ease"}}
            {...readyToStopPulseHover}
          >
            Get Started →
          </Link>
          <p style={{marginTop:16,fontSize:13,color:C.muted}}>Pay upfront, cancel anytime</p>
        </Reveal>
      </SectionBlock>

      </main>

      {/* ── FOOTER ── */}
      <footer style={{background:C.navyDeep,borderTop:"1px solid rgba(255,255,255,0.07)",padding:"28px 5%",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <Image src="/brand/logo-full.png" alt="ExpiryDesk" width={640} height={120} style={{height:28,width:"auto",display:"block",filter:"brightness(0) invert(1)",opacity:0.6}} />
          <span style={{fontSize:13,color:"rgba(255,255,255,0.22)"}}>© 2026 ExpiryDesk. All rights reserved.</span>
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:"4px 20px",alignItems:"center",justifyContent:"flex-end"}}>
          <a href="#how-it-works" className="ed-focusable" style={{...footerTextLinkStyle,padding:"10px 4px",minHeight:44,display:"inline-flex",alignItems:"center",boxSizing:"border-box"}} {...withHoverColor("rgba(255,255,255,0.3)")}>How It Works</a>
          <a href="#pricing" className="ed-focusable" style={{...footerTextLinkStyle,padding:"10px 4px",minHeight:44,display:"inline-flex",alignItems:"center",boxSizing:"border-box"}} {...withHoverColor("rgba(255,255,255,0.3)")}>Pricing</a>
          <Link href="/privacy" className="ed-focusable" style={{...footerTextLinkStyle,padding:"10px 4px",minHeight:44,display:"inline-flex",alignItems:"center",boxSizing:"border-box"}} {...withHoverColor("rgba(255,255,255,0.3)")}>Privacy</Link>
          <Link href="/terms" className="ed-focusable" style={{...footerTextLinkStyle,padding:"10px 4px",minHeight:44,display:"inline-flex",alignItems:"center",boxSizing:"border-box"}} {...withHoverColor("rgba(255,255,255,0.3)")}>Terms</Link>
          <Link href="/dashboard" className="ed-focusable" style={{...footerTextLinkStyle,padding:"10px 4px",minHeight:44,display:"inline-flex",alignItems:"center",boxSizing:"border-box"}} {...withHoverColor("rgba(255,255,255,0.3)")}>Sign In</Link>
        </div>
      </footer>
    </div>
  );
}
